import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import Dashboard from './pages/dashboard';
import AccessManagementPage from './pages/access-management';
import SignIn from './pages/auth/SignIn';
import ForgotPassword from './pages/auth/ForgotPassword';
import VerifyOtp from './pages/auth/VerifyOtp';
import ResetPassword from './pages/auth/ResetPassword';
import AdminUsers from './pages/admin/Users';
import AdminDashboard from './pages/admin-dashboard';
import InventoryPage from './pages/inventory';
import AccountingPage from './pages/accounting';
import HospitalSettingsPage from './pages/hospital-settings';
import UsersPage from './pages/users';
import DoctorDashboard from './pages/doctor-dashboard';
import NurseDashboard from './pages/nurse-dashboard';
import ReceptionistDashboard from './pages/receptionist-dashboard';
import HRManagementPage from './pages/hr-management';
import FinancePage from './pages/finance';
import AdminIAMDashboard from './pages/admin-iam';
import UsersListPage from './pages/admin-iam/pages/users';
import UserDetailPage from './pages/admin-iam/pages/users/[id]';
import CreateUserPage from './pages/admin-iam/pages/users/new';
import AppointmentScheduling from './pages/appointment-scheduling';
import ProtectedRoute from './components/guards/ProtectedRoute';
import RoleGuard from './components/guards/RoleGuard';
import DoctorAuthGuard from './components/guards/DoctorAuthGuard';
import RoleDashboard from './components/RoleDashboard';
import LandingPage from './pages/landing';
import AuthGuard from './components/guards/AuthGuard';
import { useNavigationGuard } from './hooks/useNavigationGuard';

// Wrapper component to use navigation guard inside Router context
function NavigationGuardWrapper({ children }) {
  useNavigationGuard();
  return children;
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <NavigationGuardWrapper>
      <RouterRoutes>
  {/* Public Landing Page - redirect to dashboard if authenticated */}
  <Route path="/" element={
    <AuthGuard>
      <LandingPage />
    </AuthGuard>
  } />
  
  {/* Authentication Routes - prevent access when logged in */}
  <Route path="/signin" element={
    <AuthGuard>
      <SignIn />
    </AuthGuard>
  } />

  <Route path="/forgot" element={
    <AuthGuard>
      <ForgotPassword />
    </AuthGuard>
  } />
  <Route path="/verify-otp" element={
    <AuthGuard>
      <VerifyOtp />
    </AuthGuard>
  } />
  <Route path="/reset-password" element={
    <AuthGuard>
      <ResetPassword />
    </AuthGuard>
  } />
  
  {/* Doctor Dashboard Route */}
  <Route path="/doctor/dashboard" element={
    <DoctorAuthGuard>
      <DoctorDashboard />
    </DoctorAuthGuard>
  } />
  
  {/* Nurse Dashboard Route */}
  <Route path="/nurse/dashboard" element={
    <ProtectedRoute>
      <RoleGuard allowed={["Nurse"]}>
        <NurseDashboard />
      </RoleGuard>
    </ProtectedRoute>
  } />
  
  {/* Receptionist Dashboard Route */}
  <Route path="/receptionist/dashboard" element={
    <ProtectedRoute>
      <RoleGuard allowed={["Receptionist"]}>
        <ReceptionistDashboard />
      </RoleGuard>
    </ProtectedRoute>
  } />
  {/* Admin Users Route (protected) */}
  <Route path="/admin/users" element={
    <ProtectedRoute>
      <RoleGuard allowed={["Admin"]}>
        <AdminUsers />
      </RoleGuard>
    </ProtectedRoute>
  } />
  
  {/* Admin Dashboard Route (protected) */}
  <Route path="/admin/dashboard" element={
    <ProtectedRoute>
      <RoleGuard allowed={["Admin"]}>
        <AdminDashboard />
      </RoleGuard>
    </ProtectedRoute>
  } />
  
  {/* Admin IAM Routes (protected) */}
  <Route path="/admin-iam" element={
    <ProtectedRoute>
      <RoleGuard allowed={["Admin"]}>
        <AdminIAMDashboard />
      </RoleGuard>
    </ProtectedRoute>
  } />
  <Route path="/admin-iam/users" element={
    <ProtectedRoute>
      <RoleGuard allowed={["Admin"]}>
        <UsersListPage />
      </RoleGuard>
    </ProtectedRoute>
  } />
  <Route path="/admin-iam/users/new" element={
    <ProtectedRoute>
      <RoleGuard allowed={["Admin"]}>
        <CreateUserPage />
      </RoleGuard>
    </ProtectedRoute>
  } />
  <Route path="/admin-iam/users/:userId" element={
    <ProtectedRoute>
      <RoleGuard allowed={["Admin"]}>
        <UserDetailPage />
      </RoleGuard>
    </ProtectedRoute>
  } />
  

  {/* Main Application Routes (protected) */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/accounting" element={
          <ProtectedRoute>
            <RoleGuard allowed={["Admin"]}>
              <AccountingPage />
            </RoleGuard>
          </ProtectedRoute>
        } />
        <Route path="/access-management" element={
          <ProtectedRoute>
            <RoleGuard allowed={["Admin"]}>
              <AccessManagementPage />
            </RoleGuard>
          </ProtectedRoute>
        } />
        <Route path="/inventory" element={
          <ProtectedRoute>
            <RoleGuard allowed={["Admin", "Nurse", "Doctor"]}>
              <InventoryPage />
            </RoleGuard>
          </ProtectedRoute>
        } />
        <Route path="/hr" element={
          <ProtectedRoute>
            <RoleGuard allowed={["Admin"]}>
              <HRManagementPage />
            </RoleGuard>
          </ProtectedRoute>
        } />
        <Route path="/finance" element={
          <ProtectedRoute>
            <RoleGuard allowed={["Admin"]}>
              <FinancePage />
            </RoleGuard>
          </ProtectedRoute>
        } />
        <Route path="/hospital-settings" element={
          <ProtectedRoute>
            <RoleGuard allowed={["Admin"]}>
              <HospitalSettingsPage />
            </RoleGuard>
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute>
            <RoleGuard allowed={["Admin"]}>
              <UsersPage />
            </RoleGuard>
          </ProtectedRoute>
        } />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </NavigationGuardWrapper>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
